import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { SessionProvider, useSession } from '@/api/session';
import { useApi } from '@/api/useApi';
import { Shell } from '@/components/Shell';
import { Loading, Notice, Page } from '@/components/ui';
import { AcceptInvitationPage } from '@/routes/AcceptInvitation';
import { ApprovalsPage } from '@/routes/Approvals';
import { AttentionPage } from '@/routes/Attention';
import { CareTeamPage } from '@/routes/CareTeam';
import { SchedulePage } from '@/routes/Schedule';
import { ClientRecordPage } from '@/routes/ClientRecord';
import { ClientsPage } from '@/routes/Clients';
import { FeedbackCasePage } from '@/routes/FeedbackCase';
import { FeedbackPage } from '@/routes/Feedback';
import { NoteDetailPage } from '@/routes/NoteDetail';
import { NotesPage } from '@/routes/Notes';
import { OnboardingPage } from '@/routes/Onboarding';
import { PlanEditorPage } from '@/routes/PlanEditor';
import { QualityPage } from '@/routes/Quality';
import { SignInPage } from '@/routes/SignIn';
import { TeamPage } from '@/routes/Team';

type OnboardingState = { policy_approved: boolean; baa_accepted: boolean };

function Gate() {
  const { user, loading } = useSession();
  // `/policy` sits outside both gates, so this is readable precisely when every
  // other route is being refused. Null path until there is an admin, because
  // hooks cannot be called conditionally.
  const setup = useApi<OnboardingState>(user?.role === 'admin' ? '/policy' : null);

  if (loading) return <Loading label="Restoring your session" />;
  if (!user) return <SignInPage />;

  // The server enforces this on every request; refusing to render the console
  // for a non-admin is a courtesy, not the control.
  if (user.role !== 'admin') {
    return (
      <Page>
        <Notice tone="attention" title="This console is for agency administrators">
          Your account is a {user.role} account. Caregivers and families use the Lumenest
          mobile app.
        </Notice>
      </Page>
    );
  }

  // Onboarding replaces the console rather than sitting inside it. Rendering the
  // shell would mean a sidebar of pages that all fail, which reads as a broken
  // product rather than an agency that has not finished signing up.
  if (setup.loading) return <Loading label="Checking this agency's setup" />;
  if (setup.data && !(setup.data.policy_approved && setup.data.baa_accepted)) {
    return <OnboardingPage onComplete={setup.reload} />;
  }

  return (
    <Shell>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<AttentionPage />} />
        <Route path="/clients" element={<ClientsPage />} />
        <Route path="/clients/:id" element={<ClientRecordPage />} />
        <Route path="/clients/:id/care-team" element={<CareTeamPage />} />
        <Route path="/approvals" element={<ApprovalsPage />} />
        <Route path="/clients/:id/plan" element={<PlanEditorPage />} />
        <Route path="/clients/:id/schedule" element={<SchedulePage />} />
        <Route path="/notes" element={<NotesPage />} />
        <Route path="/notes/:noteId" element={<NoteDetailPage />} />
        <Route path="/feedback" element={<FeedbackPage />} />
        <Route path="/feedback/:caseId" element={<FeedbackCasePage />} />
        <Route path="/quality" element={<QualityPage />} />
        <Route path="/team" element={<TeamPage />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Shell>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Outside the session gate on purpose: someone accepting an invitation
            has no account yet, so a route that requires one is circular — and
            falling through to the catch-all would send them to the dashboard,
            then to sign-in, which is a link that silently goes nowhere. */}
        <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
        <Route
          path="*"
          element={
            <SessionProvider>
              <Gate />
            </SessionProvider>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
